import { action, makeObservable, observable, runInAction } from "mobx";
import LeadAnalyticsAPI from "../api/LeadAnalyticsAPI";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  LeadConversionProbability,
  LeadOwnerLeaderboardRow,
  LeadStageAverages,
  LeadStageSummaryRow,
  LeadPipelineProbabilityStageSummaryGroup,
} from "../../../models/Dashboard/LeadAnalytics/LeadAnalytics";
import {
  defaultLeadAnalyticsFilters,
  LeadAnalyticsFilters,
} from "../../../models/Dashboard/LeadAnalytics/LeadAnalyticsFilters";

const emptyAverages: LeadStageAverages = {
  mql: 0,
  non_contactable: 0,
  open: 0,
  total_time: 0,
};

const emptySectionData = {
  summaryRows: [] as LeadStageSummaryRow[],
  probabilityStageSummary: [] as LeadPipelineProbabilityStageSummaryGroup[],
  averages: emptyAverages,
  limit: 30,
  totalCount: 0,
  totalPages: 0,
};

export class LeadAnalyticsStore {
  leadAnalyticsAPI: LeadAnalyticsAPI;
  loading = false;
  leaderboardLoading = false;
  filters: LeadAnalyticsFilters = defaultLeadAnalyticsFilters();
  selectedFinalStatus: string | null = null;
  selectedStagePipelineId: number | null = null;
  selectedConversionProbability: LeadConversionProbability | null = null;
  isFiltersExpanded = true;
  summaryRows: LeadStageSummaryRow[] = emptySectionData.summaryRows;
  probabilityStageSummary: LeadPipelineProbabilityStageSummaryGroup[] =
    emptySectionData.probabilityStageSummary;
  averages: LeadStageAverages = emptySectionData.averages;
  limit = emptySectionData.limit;
  totalCount = emptySectionData.totalCount;
  totalPages = emptySectionData.totalPages;
  leaderboardRows: LeadOwnerLeaderboardRow[] = [];
  owners: FilterValue[] = [];
  pipelines: FilterValue[] = [];
  sources: FilterValue[] = [];

  constructor() {
    this.leadAnalyticsAPI = new LeadAnalyticsAPI();
    makeObservable(this, {
      loading: observable,
      leaderboardLoading: observable,
      filters: observable,
      selectedFinalStatus: observable,
      selectedStagePipelineId: observable,
      selectedConversionProbability: observable,
      isFiltersExpanded: observable,
      summaryRows: observable,
      probabilityStageSummary: observable,
      averages: observable,
      limit: observable,
      totalCount: observable,
      totalPages: observable,
      leaderboardRows: observable,
      owners: observable,
      pipelines: observable,
      sources: observable,
      setFilters: action,
      updateFilters: action,
      setSelectedFinalStatus: action,
      setSelectedStagePipelineId: action,
      setSelectedConversionProbability: action,
      clearPivotSelection: action,
      setIsFiltersExpanded: action,
      setLoading: action,
      setLeaderboardLoading: action,
      resetFilters: action,
      fetchLeadStageSummary: action,
      fetchLeadOwnerLeaderboard: action,
      fetchOwners: action,
      fetchPipelines: action,
      fetchSources: action,
    });
  }

  setFilters = (filters: LeadAnalyticsFilters) => {
    this.filters = filters;
  };

  updateFilters = (updater: (prev: LeadAnalyticsFilters) => LeadAnalyticsFilters) => {
    this.filters = updater(this.filters);
  };

  setSelectedFinalStatus = (status: string | null) => {
    this.selectedFinalStatus = status;
  };

  setSelectedStagePipelineId = (pipelineId: number | null) => {
    this.selectedStagePipelineId = pipelineId;
  };

  setSelectedConversionProbability = (probability: LeadConversionProbability | null) => {
    this.selectedConversionProbability = probability;
  };

  clearPivotSelection = () => {
    this.selectedFinalStatus = null;
    this.selectedStagePipelineId = null;
    this.selectedConversionProbability = null;
  };

  setIsFiltersExpanded = (expanded: boolean) => {
    this.isFiltersExpanded = expanded;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setLeaderboardLoading = (loading: boolean) => {
    this.leaderboardLoading = loading;
  };

  resetFilters = () => {
    this.filters = defaultLeadAnalyticsFilters();
    this.clearPivotSelection();
  };

  fetchLeadStageSummary = async (filters: LeadAnalyticsFilters) => {
    try {
      const response = await this.leadAnalyticsAPI.getLeadStageSummary(filters);
      runInAction(() => {
        this.summaryRows = response.list;
        this.probabilityStageSummary = response.probability_stage_summary ?? [];
        this.averages = response.averages;
        this.limit = response.limit;
        this.totalCount = response.total_count;
        this.totalPages = response.total_pages;
        this.filters = { ...filters, section: response.section || filters.section };
      });
    } catch (error) {
      throw error;
    }
  };

  fetchLeadOwnerLeaderboard = async (filters: LeadAnalyticsFilters) => {
    try {
      this.setLeaderboardLoading(true);
      if (this.owners.length === 0) {
        await this.fetchOwners();
      }
      const response = await this.leadAnalyticsAPI.getLeadOwnerLeaderboard(
        filters,
        this.owners,
      );
      runInAction(() => {
        this.leaderboardRows = response.list;
      });
    } catch (error) {
      runInAction(() => {
        this.leaderboardRows = [];
      });
      throw error;
    } finally {
      runInAction(() => {
        this.leaderboardLoading = false;
      });
    }
  };

  fetchOwners = async () => {
    try {
      const owners = await this.leadAnalyticsAPI.getLeadOwners();
      runInAction(() => {
        this.owners = owners;
      });
    } catch (error) {
      throw error;
    }
  };

  fetchPipelines = async () => {
    try {
      const pipelines = await this.leadAnalyticsAPI.getLeadPipelines();
      runInAction(() => {
        this.pipelines = pipelines;
      });
    } catch (error) {
      throw error;
    }
  };

  fetchSources = async () => {
    try {
      const sources = await this.leadAnalyticsAPI.getLeadSources();
      runInAction(() => {
        this.sources = sources;
      });
    } catch (error) {
      throw error;
    }
  };
}
